/**
 * Helper pour ouvrir/télécharger des PDFs authentifiés en cross-domain.
 *
 * Problème résolu :
 * - Le frontend (maison-thai.ch) et le backend (emergent.host) sont sur des
 *   domaines différents.
 * - Les cookies de session httpOnly ne sont envoyés que via `fetch(..., {credentials:"include"})`.
 * - `window.open(url)` direct → 401 (cookies non transmis).
 * - `window.open(blobUrl)` APRÈS un await fetch → bloqué par le popup blocker
 *   (Safari toujours, Chrome parfois) car ce n'est plus dans le contexte d'un
 *   user gesture synchrone.
 *
 * Solution : ouvrir la fenêtre vide IMMÉDIATEMENT au clic (sync), puis injecter
 * le blob URL une fois reçu. Si la fenêtre est quand même bloquée, fallback en
 * téléchargement classique via un <a download>.
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function appendCacheBuster(url) {
  return `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
}

/**
 * Télécharge un PDF authentifié et l'affiche selon `action`.
 *
 * @param {object} opts
 * @param {string} opts.url - Chemin relatif au backend (ex: "/api/operator-guide.pdf"), ou URL absolue.
 * @param {"preview"|"download"} opts.action - "preview" ouvre dans un onglet, "download" force le téléchargement.
 * @param {string} opts.filename - Nom du fichier en cas de téléchargement.
 * @param {object} [opts.fetchOptions] - Options additionnelles (method, body, headers).
 * @returns {Promise<void>}
 */
export async function openAuthenticatedPdf({ url, action = "preview", filename = "document.pdf", fetchOptions = {} }) {
  // 1) Ouvrir une fenêtre vide IMMÉDIATEMENT (sync) pour ne pas se faire bloquer
  //    par le popup blocker. Uniquement si action === "preview".
  let win = null;
  if (action === "preview") {
    win = window.open("about:blank", "_blank");
  }

  const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
  const finalUrl = appendCacheBuster(fullUrl);

  try {
    const r = await fetch(finalUrl, {
      credentials: "include",
      ...fetchOptions,
      headers: { ...(fetchOptions.headers || {}) },
    });
    if (!r.ok) {
      if (win) win.close();
      throw new Error(`HTTP ${r.status} — ${r.statusText || "réponse inattendue"}`);
    }
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);

    if (action === "preview") {
      if (win && !win.closed) {
        win.location.href = blobUrl;
      } else {
        // Popup bloquée → on déclenche un download de secours
        triggerDownload(blobUrl, filename);
      }
    } else {
      triggerDownload(blobUrl, filename);
    }

    // Libération mémoire après affichage (le viewer a chargé son contenu)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
}

function triggerDownload(blobUrl, filename) {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
